import {useState, useCallback, useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';

import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {SearchRegularIcon} from 'shells/icon/svg/search-regular-icon';
import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';
import {useDebounce} from 'shells/utils/hooks/use-debounce';

import {KeyboardShortcutsModal} from '../../../setup/nav/keyboard-shortcuts/keyboard-shortcuts-modal';

import {analyticsClient} from '../../../../analytics-client';
import {appHistory} from '../../../../history';
import {readLocalStorage, writeLocalStorage} from '../../../../util/localStorage';

import {useGetSearchResults} from '../graphql/hooks/use-get-search-results';
import {CommandPaletteContent} from '../search-bar/command-palette/command-palette-content';
import {KeyboardKey} from '../search-bar/popover/keyboard-key';
import {KeyboardShortcuts} from '../search-bar/popover/keyboard-shortcuts';
import {PopoverContent} from '../search-bar/popover/popover-content';

import {useAiSearch} from './use-ai-search';
import {useGhostText} from './use-ghost-text';
import {useKeyboardNavigation} from './use-keyboard-navigation';
import {usePlaceholderHints} from './use-placeholder-hints';

import styles from './command-palette-modal.module.css';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

type Mode = 'search' | 'commands';

export function CommandPaletteModal(props: Props) {
    const {isOpen, onClose} = props;

    const [searchValue, setSearchValue] = useState('');
    const [mode, setModeState] = useState<Mode>(
        () => (readLocalStorage('commandPaletteMode') as Mode) || 'search'
    );
    const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false);

    const setMode = useCallback((newMode: Mode) => {
        setModeState(newMode);
        writeLocalStorage('commandPaletteMode', newMode);
    }, []);
    const debouncedSearchValue = useDebounce(searchValue, 300);
    const isTyping = searchValue !== debouncedSearchValue;

    const {
        leadResults,
        accountResults,
        contactResults,
        recentResults,
        isLoading: isSearching,
        refetch,
    } = useGetSearchResults({
        query: mode === 'search' ? debouncedSearchValue : '',
    });

    const isEntityLoading = isTyping || isSearching;

    const hasRecentResults = Boolean(
        (recentResults && recentResults.accounts.length > 0) ||
            (recentResults && recentResults.contacts.length > 0) ||
            (recentResults && recentResults.leads.length > 0)
    );

    const hasSearchResults =
        (leadResults && leadResults.length > 0) ||
        (accountResults && accountResults.length > 0) ||
        (contactResults && contactResults.length > 0);

    const inputRef = useRef<HTMLInputElement>(null);
    const searchValueRef = useRef(searchValue);
    searchValueRef.current = searchValue;

    const placeholderState = usePlaceholderHints(mode === 'search', isOpen, searchValue.length > 0);
    const ghostText = useGhostText(mode, searchValue);
    const showPlaceholder = searchValue.length === 0;

    useEffect(() => {
        if (isOpen) {
            refetch();
            requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
            analyticsClient.track(analyticsClient.EVENTS.TRACK_COMMAND_PALETTE_OPENED);
        } else {
            setSearchValue('');
        }
    }, [isOpen, refetch]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }

            if (e.key === '?' && document.activeElement !== inputRef.current) {
                e.preventDefault();
                setIsShortcutsModalOpen(true);
            }

            if ((e.metaKey || e.ctrlKey) && e.key === '1') {
                e.preventDefault();
                setMode('search');
            }
            if ((e.metaKey || e.ctrlKey) && e.key === '2') {
                e.preventDefault();
                setMode('commands');
            }

            if (e.key === 'Tab' && ghostText && document.activeElement === inputRef.current) {
                e.preventDefault();
                analyticsClient.track(
                    analyticsClient.EVENTS.TRACK_COMMAND_PALETTE_GHOST_TEXT_ACCEPTED,
                    {
                        completion: ghostText,
                        originalQuery: searchValueRef.current,
                    }
                );
                setSearchValue(ghostText);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose, setMode, ghostText]);

    useEffect(() => {
        if (!isOpen) return;

        const unlisten = appHistory.listen(() => {
            onClose();
        });

        return unlisten;
    }, [isOpen, onClose]);

    const {aiResults, isAiLoading} = useAiSearch(mode, debouncedSearchValue);
    const {activeIndex, resultRefs} = useKeyboardNavigation(isOpen, mode, debouncedSearchValue);

    if (!isOpen) {
        return null;
    }

    const isSearchMode = mode === 'search';

    return createPortal(
        <div
            className={styles.overlay}
            onMouseDown={(e) => {
                if (e.button === 0 && e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className={`${styles.modal} ${isSearchMode ? styles['modal--wide'] : ''}`}>
                <div className={styles['search-input-container']}>
                    <div className='flex align-center flex-shrink'>
                        {/* @ts-expect-error */}
                        <SearchRegularIcon size={18} color={colors.grey} alignCenter={true} />
                    </div>
                    <div className={styles['search-input-wrapper']}>
                        <input
                            ref={inputRef}
                            className={styles['search-input']}
                            type='text'
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder={placeholderState.current}
                            autoComplete='off'
                            spellCheck={false}
                        />
                        {showPlaceholder && (
                            <div className={styles['placeholder-carousel']} aria-hidden='true'>
                                {placeholderState.previous && (
                                    <span
                                        key={`exit-${placeholderState.generation}`}
                                        className={styles['placeholder-exit']}
                                    >
                                        {placeholderState.previous}
                                    </span>
                                )}
                                <span
                                    key={`enter-${placeholderState.generation}`}
                                    className={styles['placeholder-enter']}
                                >
                                    {placeholderState.current}
                                </span>
                            </div>
                        )}
                        {ghostText && searchValue.length > 0 && (
                            <span className={styles['ghost-text']} aria-hidden='true'>
                                <span style={{visibility: 'hidden'}}>{searchValue}</span>
                                {ghostText.slice(searchValue.length)}
                            </span>
                        )}
                    </div>
                    <KeyboardKey width={24}>
                        <Body size='small' color='light'>
                            esc
                        </Body>
                    </KeyboardKey>
                </div>
                <div className={styles['tab-bar']}>
                    <button
                        className={`${styles['tab-button']} ${
                            isSearchMode ? styles['tab-button--active'] : ''
                        }`}
                        onClick={() => setMode('search')}
                        type='button'
                    >
                        Search
                        <KeyboardKey width={24} height={14}>
                            <Body size='small' color='light'>
                                ⌘1
                            </Body>
                        </KeyboardKey>
                    </button>
                    <button
                        className={`${styles['tab-button']} ${
                            !isSearchMode ? styles['tab-button--active'] : ''
                        }`}
                        onClick={() => setMode('commands')}
                        type='button'
                    >
                        Commands
                        <KeyboardKey width={24} height={14}>
                            <Body size='small' color='light'>
                                ⌘2
                            </Body>
                        </KeyboardKey>
                    </button>
                </div>
                <div className='flex-grow overflow-y-auto pad-8'>
                    {isSearchMode ? (
                        <Stack spacing={8} isFullWidth isFullHeight alignItems='center'>
                            <PopoverContent
                                isLoading={isEntityLoading}
                                hasSearchResults={Boolean(hasSearchResults)}
                                hasRecentResults={hasRecentResults}
                                searchValue={debouncedSearchValue}
                                setIsSearchBarFocused={() => onClose()}
                                accountResults={accountResults}
                                contactResults={contactResults}
                                leadResults={leadResults}
                                recentResults={recentResults}
                                setSearchValue={setSearchValue}
                                hasTyped={searchValue.length > 0}
                            />
                        </Stack>
                    ) : (
                        <CommandPaletteContent
                            searchValue={debouncedSearchValue}
                            isLoading={isTyping}
                            hasTyped={searchValue.length > 0}
                            setIsSearchBarFocused={() => onClose()}
                            setSearchValue={setSearchValue}
                            activeIndex={activeIndex}
                            resultRefs={resultRefs}
                            aiSuggestions={aiResults?.suggestions ?? []}
                            isAiLoading={isAiLoading}
                        />
                    )}
                </div>
                <div className='flex align-center justify-sb gap-12 py-8 px-12 border-top'>
                    <Button
                        variant='text-secondary'
                        size='mini'
                        noPadding={true}
                        onClick={() => setIsShortcutsModalOpen(true)}
                        preventUnderline={true}
                    >
                        <Stack spacing={4} isHorizontal alignItems='center'>
                            <KeyboardKey>
                                <Body size='small' color='dark'>
                                    ?
                                </Body>
                            </KeyboardKey>
                            <Body size='small' color='dark'>
                                Keyboard shortcuts
                            </Body>
                        </Stack>
                    </Button>
                    <div className='flex align-center gap-12'>
                        <Stack spacing={4} isHorizontal alignItems='center'>
                            <KeyboardKey width={28} height={14}>
                                <Body size='small' color='light'>
                                    Tab
                                </Body>
                            </KeyboardKey>
                            <Body size='small' color='light'>
                                Complete
                            </Body>
                        </Stack>
                        <KeyboardShortcuts />
                    </div>
                </div>
            </div>
            <KeyboardShortcutsModal
                isOpen={isShortcutsModalOpen}
                onClose={() => setIsShortcutsModalOpen(false)}
            />
        </div>,
        document.body
    );
}
